import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';

interface Feature {
  icon: string;
  title: string;
  description: string;
}

interface ProcessStep {
  step: number;
  title: string;
  description: string;
}

interface CaseStudy {
  title: string;
  client: string;
  industry: string;
  challenge: string;
  solution: string;
  results: {
    metric: string;
    value: string;
  }[];
  image: string;
}

interface Testimonial {
  name: string;
  role: string;
  company: string;
  content: string;
  avatar: string;
}

@Component({
  selector: 'app-whatsapp-api',
  imports: [CommonModule],
  templateUrl: './whatsapp-api.html',
  styleUrl: './whatsapp-api.css',
  standalone: true
})
export class WhatsappApi {
  features: Feature[] = [
    {
      icon: 'M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
      title: 'Enterprise API Integration',
      description: 'RESTful API with comprehensive documentation and SDKs for Python, Node.js, Java, and more. Seamlessly integrate WhatsApp into your existing systems and workflows.'
    },
    {
      icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
      title: 'AI-Powered Chatbots',
      description: 'Build intelligent conversational agents with natural language processing. Automate customer service, lead qualification, and support across multiple languages.'
    },
    {
      icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9',
      title: 'Automated Notifications',
      description: 'Send transactional alerts, order updates, appointment reminders, and delivery notifications. Keep customers informed with timely, relevant automated messages.'
    },
    {
      icon: 'M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z',
      title: 'Two-Way Conversations',
      description: 'Enable real human-to-human conversations when needed. Seamlessly hand off from bot to agent with full conversation context and history.'
    },
    {
      icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
      title: 'Rich Media & Documents',
      description: 'Share images, videos, PDFs, and other documents within conversations. Send product catalogs, invoices, receipts, and support materials seamlessly.'
    },
    {
      icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
      title: 'Advanced Analytics',
      description: 'Track message delivery, conversation metrics, chatbot performance, and customer satisfaction. Export data for business intelligence and optimization.'
    }
  ];

  process: ProcessStep[] = [
    {
      step: 1,
      title: 'API Setup & Integration',
      description: 'Get your API credentials and integrate with our SDKs or REST API. We provide detailed documentation and developer support for quick implementation.'
    },
    {
      step: 2,
      title: 'Build Conversation Flows',
      description: 'Design chatbot logic, create message templates, and configure automated workflows. Use our visual flow builder or code your own custom logic.'
    },
    {
      step: 3,
      title: 'Test & Deploy',
      description: 'Test conversations in sandbox environment before going live. Validate template approvals and ensure compliance with WhatsApp policies.'
    },
    {
      step: 4,
      title: 'Monitor & Optimize',
      description: 'Track performance metrics, analyze conversation data, and continuously improve automation. Scale seamlessly as your user base grows.'
    }
  ];

  caseStudy: CaseStudy = {
    title: 'AI-Powered Customer Support Automation',
    client: 'TechGadgets Online',
    industry: 'Consumer Electronics Retail',
    challenge: 'TechGadgets was overwhelmed with customer support inquiries about product specifications, order tracking, returns, and technical troubleshooting. Their support team couldn\'t scale to meet demand, resulting in long wait times and frustrated customers.',
    solution: 'Implemented HoneyKode\'s WhatsApp API with an AI-powered chatbot that handles common inquiries automatically. Integrated with their e-commerce platform, CRM, and knowledge base for real-time information. Human agents handle complex cases with full conversation context.',
    results: [
      { metric: 'Automated Resolution', value: '73%' },
      { metric: 'Response Time', value: '< 30 seconds' },
      { metric: 'Support Cost', value: '64% reduction' },
      { metric: 'Customer Satisfaction', value: '4.7/5.0' },
      { metric: 'Agent Productivity', value: '3.2x increase' },
      { metric: 'Conversations/Month', value: '180K+' }
    ],
    image: 'https://images.unsplash.com/photo-1556155092-8707de31f9c4?w=800&h=500&fit=crop'
  };

  testimonial: Testimonial = {
    name: 'Marcus Williams',
    role: 'CTO',
    company: 'TechGadgets Online',
    content: 'The WhatsApp API integration has been transformational for our customer support operations. Our AI chatbot handles the majority of inquiries instantly, and customers love the convenience of getting help on WhatsApp without calling or emailing. The seamless handoff to human agents when needed ensures complex issues are still resolved with a personal touch. We\'ve dramatically reduced support costs while actually improving customer satisfaction. The API is robust, well-documented, and the HoneyKode team provided excellent implementation support.',
    avatar: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=150&h=150&fit=crop'
  };
}
